import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Car, Phone, MapPin, Clock, AlertCircle } from 'lucide-react';
import { vehiclesAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AdvertisementBanner from '../components/AdvertisementBanner';

const VehicleCall = () => {
  const { qrToken } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalling, setIsCalling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [qrExpanded, setQrExpanded] = useState(false);

  useEffect(() => {
    if (qrToken) {
      loadVehicleData();
    }

    // Prevent link previews from bots (WhatsApp, iMessage, Telegram, etc.)
    const metaTags = [
      { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet' },
      { property: 'og:title', content: 'Veribil Araç Bileti' },
      { property: 'og:description', content: 'Araç biletinizi görüntülemek için tıklayın' },
      { name: 'twitter:card', content: 'summary' },
    ];

    const addedMetas = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    // Cleanup meta tags on unmount
    return () => {
      addedMetas.forEach(meta => {
        if (meta.parentNode) {
          meta.parentNode.removeChild(meta);
        }
      });
    };
  }, [qrToken]);

  const loadVehicleData = async () => {
    setIsLoading(true);
    setError('');
    try {
      // Use the same customer ticket API for consistent data
      const response = await fetch(`${process.env.REACT_APP_API_URL}/customer/ticket/${qrToken}`);

      if (!response.ok) {
        throw new Error('Araç bulunamadı');
      }

      const data = await response.json();
      setVehicle(data.data);

      // Note: We only send notification when customer actually calls the vehicle (not just viewing)
      // This prevents unnecessary notifications when customer is just checking their vehicle status

    } catch (err) {
      setError(err.message || 'Araç bilgileri alınamadı');
    } finally {
      setIsLoading(false);
    }
  };

  // Telefon numarası formatlama fonksiyonu
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    // Sadece rakamları al
    const cleanNumber = phoneNumber.replace(/\D/g, '');

    // Türk telefon numarası formatı: 0XXX XXX XX XX
    if (cleanNumber.length === 11 && cleanNumber.startsWith('0')) {
      return `${cleanNumber.slice(0, 4)} ${cleanNumber.slice(4, 7)} ${cleanNumber.slice(7, 9)} ${cleanNumber.slice(9, 11)}`;
    }

    // Alan kodu ile: 0216 XXX XX XX
    if (cleanNumber.length === 11 && cleanNumber.startsWith('02')) {
      return `${cleanNumber.slice(0, 4)} ${cleanNumber.slice(4, 7)} ${cleanNumber.slice(7, 9)} ${cleanNumber.slice(9, 11)}`;
    }

    // Orijinal numarayı döndür
    return phoneNumber;
  };

  const handleCallVehicle = async () => {
    if (!vehicle) {
      return;
    }

    setIsCalling(true);
    setError('');
    setSuccess('');

    try {
      // Use the customer API endpoint instead of the authenticated vehicles API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/customer/call/${qrToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Araç çağırma işlemi başarısız');
      }

      const data = await response.json();
      setSuccess('Araç başarıyla çağrıldı! Personel bilgilendirildi.');
      setVehicle(prev => ({ ...prev, status: 'called' }));

      console.log('Vehicle called successfully:', data);
    } catch (err) {
      setError(err.message || 'Araç çağırma işlemi başarısız');
      console.error('Call vehicle error:', err);
    } finally {
      setIsCalling(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'text-green-600 bg-green-100';
      case 'called': return 'text-orange-600 bg-orange-100';
      case 'washing': return 'text-yellow-600 bg-yellow-100';
      case 'parked': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'ready': return 'Aracınız teslime hazır!';
      case 'called': return 'Aracınız çağrıldı ve teslim için hazırlanıyor.';
      case 'washing': return 'Aracınız şu anda yıkanıyor.';
      case 'parked': return 'Aracınız yıkanmış ve park edilmiş.';
      case 'received': return 'Aracınız teslim alınmış ve sırada bekliyor.';
      default: return 'Durum bilinmiyor.';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Araç bilgileri yüklenliyor...</p>
        </div>
      </div>
    );
  }

  if (error && !vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Araç Bulunamadı</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={loadVehicleData}
              className="btn-primary"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If vehicle is delivered, show simplified view
  if (vehicle && vehicle.status === 'delivered') {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 lg:px-8">
        <div className="max-w-sm sm:max-w-md mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Header with Veribil Logo */}
          <div className="bg-gradient-to-r from-[#7a2c2c] to-[#5a1f1f] text-white p-4 sm:p-5 relative">
            <div className="flex items-center justify-between">
              {/* Left side - Veribil Logo & Name */}
              <div className="flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white w-5 h-5 sm:w-6 sm:h-6">
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"></path>
                    <circle cx="7" cy="17" r="2"></circle>
                    <path d="M9 17h6"></path>
                    <circle cx="17" cy="17" r="2"></circle>
                  </svg>
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl font-bold leading-none">VERIBIL</h1>
                  <p className="text-red-100 text-xs sm:text-sm mt-0.5">Teknoloji</p>
                </div>
              </div>

              {/* Right side - Location & Status */}
              <div className="text-right">
                <div className="text-white text-sm sm:text-base font-medium mb-1">{vehicle.location_name}</div>
                <div className="text-white font-semibold text-xs sm:text-sm">Araç Teslim Edildi</div>
              </div>
            </div>
          </div>

          {/* Vehicle Delivered Message */}
          <div className="bg-green-50 border-l-4 border-green-400 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg sm:text-xl font-semibold text-green-800">
                  Araç Teslim Edildi
                </h3>
                <p className="text-sm sm:text-base text-green-700 mt-2">
                  {vehicle.license_plate} plakalı aracınız teslim edilmiştir.
                </p>
              </div>
            </div>
          </div>

          {/* Advertisement Banner for Delivered Status */}
          {vehicle?.location_id && vehicle?.ad_enabled === 1 && (
            <div className="p-4 sm:p-6 pt-0">
              <AdvertisementBanner locationId={vehicle.location_id} />
            </div>
          )}

          {/* Basic Info */}
          <div className="p-4 sm:p-6 pt-0">
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <div className="flex items-center">
                  <Car className="h-4 w-4 text-gray-500 mr-2" />
                  <span className="text-gray-700 text-sm">Plaka</span>
                </div>
                <span className="font-bold text-base">{vehicle.license_plate}</span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 text-gray-500 mr-2" />
                  <span className="text-gray-700 text-sm">Lokasyon</span>
                </div>
                <span className="font-semibold text-sm">{vehicle.location_name}</span>
              </div>

              {/* Reference Number */}
              <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-red-50 rounded-lg text-center border-2 border-red-200">
                <p className="text-sm text-gray-700 mb-1 font-medium">Referans No</p>
                <p className="font-mono font-bold text-2xl text-[#7a2c2c]">{vehicle.reference_code || qrToken.toUpperCase()}</p>
              </div>

              {/* Contact Information */}
              {(vehicle.location_phone || vehicle.location_address) && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-3">İletişim Bilgileri</p>
                  {vehicle.location_phone && (
                    <div className="flex items-center mb-2">
                      <Phone className="h-4 w-4 text-gray-500 mr-2" />
                      <a href={`tel:${vehicle.location_phone}`} className="text-sm text-[#7a2c2c] hover:underline">
                        {vehicle.location_phone}
                      </a>
                    </div>
                  )}
                  {vehicle.location_address && (
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 text-gray-500 mr-2 mt-0.5" />
                      <p className="text-sm text-gray-600">{vehicle.location_address}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 p-3 sm:p-4 text-center text-xs sm:text-sm text-gray-600">
            <p>Veribil teknoloji tarafından geliştirilmiştir.</p>
            <p className="mt-1">İşlem başarıyla tamamlandı.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 lg:px-8">
      <div className="max-w-sm sm:max-w-md mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header with Veribil Logo */}
        <div className="bg-gradient-to-r from-[#7a2c2c] to-[#5a1f1f] text-white p-4 sm:p-5 relative">
          <div className="flex items-center justify-between">
            {/* Left side - Veribil Logo & Name */}
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white w-5 h-5 sm:w-6 sm:h-6">
                  <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"></path>
                  <circle cx="7" cy="17" r="2"></circle>
                  <path d="M9 17h6"></path>
                  <circle cx="17" cy="17" r="2"></circle>
                </svg>
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold leading-none">VERIBIL</h1>
                <p className="text-red-100 text-xs sm:text-sm mt-0.5">Teknoloji</p>
              </div>
            </div>

            {/* Right side - Location & Ticket Type */}
            <div className="text-right">
              <div className="text-white text-sm sm:text-base font-medium mb-1">{vehicle?.location_name}</div>
              <div className="text-white font-semibold text-xs sm:text-sm">Otopark Bileti</div>
            </div>
          </div>
        </div>

        {/* Vehicle Call Status Banner - Show only when vehicle is called */}
        {vehicle?.status === 'called' && (
          <div className="bg-green-500 text-white px-4 py-3">
            <div className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-semibold text-sm sm:text-base">Aracınız çağrıldı!</span>
            </div>
            <p className="text-center text-xs sm:text-sm mt-1 text-green-100">
              Personel bilgilendirildi, lütfen araç teslim noktasına gelip aracınızı alınız.
            </p>
          </div>
        )}

        {/* Main content */}
        <div className="p-4 sm:p-6">
          {/* Call vehicle button - Show ABOVE QR code */}
          {vehicle?.status !== 'called' && vehicle?.status !== 'delivered' && !success && (
            <div className="mb-4 sm:mb-6">
              <button
                onClick={handleCallVehicle}
                disabled={isCalling}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center text-lg"
              >
                {isCalling ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Araç Çağrılıyor...
                  </>
                ) : (
                  <>
                    <Phone className="h-6 w-6 mr-2" />
                    Aracımı Çağır
                  </>
                )}
              </button>
            </div>
          )}

          {/* QR Code - With thin border and padding */}
          <div className="text-center mb-4 sm:mb-6">
            <div
              className={`inline-block bg-white border-2 border-gray-400 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md p-3 ${
                qrExpanded ? 'fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center rounded-none border-none' : ''
              }`}
              onClick={() => setQrExpanded(!qrExpanded)}
            >
              {qrExpanded && (
                <div className="absolute top-4 right-4 text-white" onClick={(e) => {
                  e.stopPropagation();
                  setQrExpanded(false);
                }}>
                  <svg className="w-8 h-8 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <div className={qrExpanded ? 'bg-white p-4 rounded-lg' : ''}>
                <QRCodeSVG
                  value={`veribil_${qrToken}`}
                  size={qrExpanded ? 300 : window.innerWidth < 640 ? 150 : 180}
                  level="M"
                  includeMargin={false}
                  imageSettings={{
                    src: "data:image/svg+xml;base64," + btoa('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="48" fill="#7a2c2c"/><path d="M25 45 C25 38, 30 33, 37 33 L63 33 C70 33, 75 38, 75 45 L75 55 C75 62, 70 67, 63 67 L37 67 C30 67, 25 62, 25 55 Z" fill="white"/><circle cx="35" cy="58" r="6" fill="#7a2c2c"/><circle cx="65" cy="58" r="6" fill="#7a2c2c"/><circle cx="35" cy="58" r="3" fill="white"/><circle cx="65" cy="58" r="3" fill="white"/><path d="M40 40 C40 38, 42 36, 44 36 L56 36 C58 36, 60 38, 60 40 L60 45 L40 45 Z" fill="#7a2c2c"/></svg>'),
                    height: qrExpanded ? 70 : 45,
                    width: qrExpanded ? 70 : 45,
                    excavate: false
                  }}
                />
              </div>
            </div>
            <div className="flex items-center justify-center mt-2">
              <p className="text-sm text-gray-700 font-medium">
                QR kodunuzu valeye gösteriniz
              </p>
            </div>
            <div className="flex items-center justify-center mt-1">
              <p className="text-xs text-gray-400 italic">
                (Büyütmek için üzerine tıklayın)
              </p>
            </div>
          </div>

          {/* Dynamic Advertisement Banner */}
          {vehicle?.location_id && vehicle?.ad_enabled === 1 && <AdvertisementBanner locationId={vehicle.location_id} />}

          {/* Ticket Details - Same as CustomerTicket */}
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <div className="flex items-center flex-shrink-0">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 mr-2" />
                <span className="text-gray-700 text-xs sm:text-sm">Tarih</span>
              </div>
              <span className="font-semibold text-xs sm:text-sm">{new Date(vehicle?.created_at).toLocaleDateString('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}</span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <div className="flex items-center flex-shrink-0">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 mr-2" />
                <span className="text-gray-700 text-xs sm:text-sm">Saat</span>
              </div>
              <span className="font-semibold text-xs sm:text-sm">{new Date(vehicle?.created_at).toLocaleTimeString('tr-TR', {
                hour: '2-digit',
                minute: '2-digit'
              })}</span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <div className="flex items-center flex-shrink-0">
                <Car className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 mr-2" />
                <span className="text-gray-700 text-xs sm:text-sm">Plaka</span>
              </div>
              <span className="font-bold text-sm sm:text-base">{vehicle?.license_plate}</span>
            </div>

            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <div className="flex items-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="text-gray-700 text-xs sm:text-sm">Hizmet Bedeli</span>
              </div>
              <span className="font-semibold text-xs sm:text-sm">{vehicle?.amount || '0.00'} ₺</span>
            </div>
          </div>

          {/* Reference Number - Big and Bold */}
          <div className="mt-6 sm:mt-8 p-4 sm:p-5 bg-gradient-to-r from-red-50 to-red-50 rounded-lg text-center border-2 border-red-200">
            <p className="text-sm sm:text-base text-gray-700 mb-2 font-medium">REFERANS NUMARASI</p>
            <p className="font-mono font-bold text-4xl sm:text-5xl text-[#7a2c2c]">{vehicle?.reference_code}</p>
          </div>

          {error && (
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                <p className="text-red-700 text-xs sm:text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Contact info - Enhanced */}
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-2">Yardıma İhtiyacınız Var Mı?</p>
            {vehicle?.location_phone ? (
              <a
                href={`tel:${vehicle.location_phone}`}
                className="inline-flex items-center text-[#7a2c2c] font-semibold text-xs sm:text-sm hover:text-[#5a1f1f]"
              >
                <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                {formatPhoneNumber(vehicle.location_phone)}
              </a>
            ) : (
              <p className="text-xs sm:text-sm text-gray-500">Telefon bilgisi mevcut değil</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-3 sm:p-4 text-center text-xs sm:text-sm text-gray-600">
          <p>Veribil teknoloji tarafından geliştirilmiştir.</p>
          <p className="mt-1">Bu hizmet elektronik olarak sunulmaktadır.</p>
        </div>
      </div>
    </div>
  );
};

export default VehicleCall;